import { useCallback } from 'react'
import { LEAD_TAB } from 'utils/constants'

interface UseReusableTabOptions {
  tabName?: string
  onError?: (error: Error) => void
}

// Store tab references outside React lifecycle
const tabRefs = new Map<string, Window | null>()
const origin = window.location.origin

export const useReusableTab = (options: UseReusableTabOptions = {}) => {
  const { tabName = LEAD_TAB, onError } = options

  const openInReusableTab = useCallback(
    (url: string) => {
      if (!url || url === 'about:blank') {
        const error = new Error('Invalid URL provided')
        onError?.(error)
        console.warn('Invalid URL, skipping')
        return
      }

      // Get the tab reference for this tabName
      const tabRef = tabRefs.get(tabName)
      let canAccess = false

      // Check if tab exists and we can still access it
      if (tabRef && !tabRef.closed) {
        try {
          // Try to read the location (will throw if cross-origin)
          const tabOrigin = tabRef.location.origin
          canAccess = tabOrigin === origin
        } catch (e) {
          // Cross-origin - we can't access the tab anymore
          canAccess = false
        }
      }

      if (canAccess && tabRef) {
        // Same origin, we can navigate directly
        try {
          tabRef.location.href = url
          tabRef.focus()
        } catch (e) {
          const error = e instanceof Error ? e : new Error('Failed to navigate tab')
          onError?.(error)
          console.error('Failed to navigate existing tab:', e)
        }
      } else {
        // Tab is closed, cross-origin, or doesn't exist
        // Close the old tab if it exists
        if (tabRef && !tabRef.closed) {
          try {
            tabRef.close()
          } catch (e) {
            console.error('Could not close old tab:', e)
          }
        }

        // Open a fresh tab
        const newTab = window.open(url, tabName)

        if (newTab) {
          tabRefs.set(tabName, newTab)
          newTab.focus()
        } else {
          const error = new Error('Failed to open tab - popup might be blocked')
          onError?.(error)
          console.error('Failed to open tab - popup might be blocked')
        }
      }
    },
    [tabName, onError],
  )

  const closeReusableTab = useCallback(() => {
    const tabRef = tabRefs.get(tabName)
    if (tabRef && !tabRef.closed) {
      try {
        tabRef.close()
        tabRefs.delete(tabName)
      } catch (e) {
        console.error('Failed to close tab:', e)
      }
    }
  }, [tabName])

  const isTabOpen = useCallback(() => {
    const tabRef = tabRefs.get(tabName)
    return tabRef !== null && tabRef !== undefined && !tabRef.closed
  }, [tabName])

  return {
    openInReusableTab,
    closeReusableTab,
    isTabOpen,
  }
}
